import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import DataTable from "react-data-table-component";
import { customStyles } from "../../styles/dataTableStyles";
import { api } from "../../api";

function DevcoSalesTable() {
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesData, setSalesData] = useState([]);
  const [salesError, setSalesError] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const token = useSelector((state) => state.auth.token);

  // Date range state
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split("T")[0];
  });

  const fetchSalesData = async () => {
    if (!fromDate || !toDate) {
      setSalesError("Please select both from and to dates");
      return;
    }

    setSalesLoading(true);
    setSalesError(null);

    try {
      const response = await api.devco.getDevcoSalesData(
        {
          fromDate,
          toDate,
        },
        token
      );

      if (response?.success && response.data?.CONTENT) {
        const contentData = Array.isArray(response.data.CONTENT)
          ? response.data.CONTENT
          : [];
        setSalesData(contentData);
      } else {
        setSalesError("Failed to load sales data");
        setSalesData([]);
      }
    } catch (error) {
      console.error("Error fetching sales data:", error);
      setSalesError("Failed to load sales data");
      setSalesData([]);
    } finally {
      setSalesLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCurrency = (value) => {
    const numValue = parseFloat(value);
    return `$${numValue.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // DataTable columns configuration
  const columns = [
    {
      name: "Transaction ID",
      selector: (row) => row.TRANSACTION_ID,
      sortable: true,
    },
    {
      name: "Product",
      selector: (row) => row.OEM_PRODUCT_Name,
      cell: (row) => (
        <span className="add-ellip" title={row.OEM_PRODUCT_Name}>
          {row.OEM_PRODUCT_Name}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Quantity",
      selector: (row) => row.TRANSACTION_VOUCHER_QUANTITY,
      cell: (row) => (
        <span className="text-center">{row.TRANSACTION_VOUCHER_QUANTITY}</span>
      ),
      sortable: true,
    },
    {
      name: "USD Value",
      selector: (row) => row.TRANSACTION_USD_VALUE,
      cell: (row) => (
        <span className="text-success">
          {formatCurrency(row.TRANSACTION_USD_VALUE)}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.TRANSACTION_CurrentStatus,
      cell: (row) => (
        <span
          className={`badge ${
            row.TRANSACTION_CurrentStatus === "Completed successfully"
              ? "bg-success"
              : "bg-warning"
          } text-white`}
        >
          {row.TRANSACTION_CurrentStatus}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Transaction Date",
      selector: (row) => row.TRANSACTION_DATE,
      cell: (row) => formatDate(row.TRANSACTION_DATE),
      sortable: true,
    },
    {
      name: "Reference",
      selector: (row) => row.TRANSACTION_Reference,
      cell: (row) => (
        <span className="add-ellip" title={row.TRANSACTION_Reference}>
          {row.TRANSACTION_Reference}
        </span>
      ),
      sortable: true,
    },
  ];

  return (
    <div className="row mt-4">
      <div className="col-sm-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <strong>Sales Data</strong>
            </h5>
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-expanded={!isCollapsed}
              style={{
                textDecoration: "none",
                outline: "none",
                border: "none",
                background: "#5C2E91",
                borderRadius: "4px",
              }}
            >
              <i
                className={`bx ${
                  isCollapsed ? "bx-chevron-down" : "bx-chevron-up"
                }`}
                style={{ color: "white" }}
              ></i>
            </button>
          </div>
          {!isCollapsed && (
            <div className="card-body">
              {/* Date Range Filters */}
              <div className="d-flex justify-content-end align-items-center mb-3 flex-wrap gap-2">
                <div className="d-flex align-items-center">
                  <label className="mr-2">From Date:</label>
                  <input
                    type="date"
                    value={fromDate}
                    className="form-control"
                    style={{ width: "200px", marginRight: "10px" }}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                  <label className="mr-2">To Date:</label>
                  <input
                    type="date"
                    value={toDate}
                    className="form-control"
                    style={{ width: "200px", marginRight: "10px" }}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                  <button
                    className="btn btn-primary ml-2 px-4"
                    onClick={fetchSalesData}
                    disabled={salesLoading}
                  >
                    {salesLoading ? "Loading..." : "Get Data"}
                  </button>
                </div>
              </div>

              {/* Error Display */}
              {salesError && (
                <div className="alert alert-danger" role="alert">
                  {salesError}
                </div>
              )}

              {/* DataTable */}
              <div className="card card-table">
                <div>
                  <div className="reports">
                    <DataTable
                      columns={columns}
                      data={Array.isArray(salesData) ? salesData : []}
                      progressPending={salesLoading}
                      highlightOnHover
                      pointerOnHover
                      customStyles={customStyles}
                      responsive
                      noHeader={false}
                      noTableHead={false}
                      wrap
                      noDataComponent={
                        <div className="text-center text-muted py-4">
                          <p>
                            No sales data found for the selected date range.
                          </p>
                        </div>
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DevcoSalesTable;
